import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const SELECT = "SELECT";

const relationOptions = [
  "FATHER", "MOTHER", "HUSBAND", "WIFE", "SON",
  "DAUGHTER", "BROTHER", "SISTER", "OTHER",
];

const qualificationOptions = [
  ["9TH", "Up to 9th Class passed"],
  ["10TH", "10th Class passed"],
  ["GRADUATE", "Graduate (Gen.)"],
  ["PGRADUATE", "Post Graduate (Gen.)"],
  ["MEDGRADUATE", "Medical Graduate/Post Graduate"],
  ["ENGGRADUATE", "Engineering Graduate/Post Graduate"],
  ["LAWGRADUATE", "Law Graduate/Post Graduate"],
  ["CA", "CA/ICWA/MBA/CFA"],
  ["COMPUTER", "Computer Degree/Diploma/MCA"],
  ["OPROFFESIONAL", "Other Professional Degree/Diploma"],
  ["ILLITERATE", "Illiterate"],
];

const occupationOptions = [
  ["STATE_GOVT", "State Govt."],
  ["CENTRAL_GOVT", "Central Govt."],
  ["PUBLIC_SECTOR_U", "Public Sector Undertaking"],
  ["DEFENCE", "Defence"],
  ["PRIVATE_SECTOR", "Private Sector"],
  ["INDUSTRIALIST", "Industrialist"],
  ["TRADE_SECTOR", "Trade Sector"],
  ["SERVICE_SECTOR", "Service Sector"],
  ["MIGRANT_LABOUR", "Migrant Labour"],
  ["CONTRACTOR", "Contractor"],
  ["JEWELLER", "Jeweller / Bullion Trader"],
  ["PAWN_SHOP", "Pawn Shop"],
  ["IMPORT_EXPORT", "Import / Export Customer"],
  ["OTHER_SELF_EMPLOYER", "Other Self Employed"],
  ["MEDICAL_PROFFESIONAL", "Medical Professional"],
  ["LEGAL_PROFEESIONAL", "Legal Professional"],
  ["CA_ICWA", "CA/ICWA/Taxation/Finance"],
  ["ENGINEER", "Engineer/Architect/Technical Consultant"],
  ["RETIRED", "Retired"],
  ["JOURNALIST", "Journalist"],
  ["HOUSEWIFE", "Housewife"],
  ["STUDENT", "Student"],
  ["SHARE", "Share and Stock Broker"],
  ["OTHER_PROFFESIONAL", "Other Professional"],
  ["AGRICULTURE", "Agriculture"],
  ["POLITICAL", "Political / Social Worker"],
  ["NOT_CATEGORISED", "Not categorised"],
];

const makePersonalFields = (prefix) => [
  { name: `${prefix}cifno`, label: "CIF Number", maxLength: 11, numeric: true },
  { name: `${prefix}acno`, label: "Account Number", maxLength: 11, numeric: true },
  { name: `${prefix}name`, label: "Applicant Name", maxLength: 72, alphaOnly: true, required: true },
  { name: `${prefix}dob`, label: "Date of Birth", type: "date", required: true },
  { name: `${prefix}gender`, label: "Gender", type: "select", options: ["MALE", "FEMALE"], required: true },
  { name: `${prefix}mstatus`, label: "Marital Status", type: "select", options: ["MARRIED", "UNMARRIED"], required: true },
  { name: `${prefix}aadhaarno`, label: "Aadhaar Number", maxLength: 12, numeric: true, required: true },
  { name: `${prefix}pan`, label: "PAN Number", maxLength: 10, uppercase: true },
  { name: `${prefix}fname`, label: "Father's Name", maxLength: 36, alphaOnly: true, required: true },
];

const makeGuardianFields = (prefix) => [
  { name: `${prefix}gname`, label: "Guardian's Name", maxLength: 36, alphaOnly: true, required: true },
  { name: `${prefix}grelation`, label: "Guardian's Relation", type: "select", options: relationOptions, required: true },
];

const makeAddressFields = (prefix) => [
  { name: `${prefix}add1`, label: "Address Line 1", maxLength: 36, required: true },
  { name: `${prefix}add2`, label: "Address Line 2", maxLength: 36, required: true },
  { name: `${prefix}city`, label: "City", maxLength: 13, required: true },
  { name: `${prefix}dist`, label: "District", maxLength: 23, required: true },
  { name: `${prefix}state`, label: "State", maxLength: 13, required: true },
  { name: `${prefix}pin`, label: "PIN", maxLength: 6, numeric: true, required: true },
  { name: `${prefix}bplace`, label: "Birth Place", maxLength: 13, required: true },
  { name: `${prefix}mobile`, label: "Mobile", maxLength: 10, numeric: true, required: true },
  { name: `${prefix}income`, label: "Annual Income", maxLength: 8, numeric: true, required: true },
  { name: `${prefix}religion`, label: "Religion", type: "select", options: ["HINDU", "MUSLIM", "CHRISTIAN", "SIKH"], required: true },
  { name: `${prefix}category`, label: "Category", type: "select", options: ["GENERAL", "OBC", "SC", "ST"], required: true },
  { name: `${prefix}qualification`, label: "Qualification", type: "select", options: qualificationOptions, required: true },
  { name: `${prefix}occupation`, label: "Occupation", type: "select", options: occupationOptions, required: true },
];

const firstPersonalFields = makePersonalFields("fa");
const firstGuardianFields = makeGuardianFields("fa");
const firstAddressFields = makeAddressFields("fa");

const secondPersonalFields = makePersonalFields("sa");
const secondGuardianFields = makeGuardianFields("sa");
const secondAddressFields = makeAddressFields("sa");

const nominationControlFields = [
  { name: "isnomination", label: "Is Nomination?", type: "select", options: ["YES", "NO"], required: true },
];

const nomineeFields = [
  { name: "nname", label: "Nominee Name", maxLength: 36, alphaOnly: true, required: true },
  { name: "nrelation", label: "Relation", type: "select", options: relationOptions, required: true },
  { name: "ndob", label: "Date of Birth", type: "date", required: true },
];

const nomineeGuardianFields = [
  { name: "ngname", label: "Guardian's Name", maxLength: 36, alphaOnly: true, required: true },
  { name: "ngage", label: "Guardian's Age", maxLength: 2, numeric: true, required: true },
];

const optionalFields = [
  { name: "mod", label: "Mode of Operation", type: "select", options: [
    ["SELF", "Self"],
    ["EOS", "Either or Survivor"],
    ["JOINTLY", "Jointly Operated"],
    ["OTHER", "Other"],
  ], required: true },
  { name: "atm", label: "ATM", type: "select", options: ["YES", "NO"], required: true },
  { name: "cbook", label: "Cheque Book", type: "select", options: ["YES", "NO"], required: true },
  { name: "ibanking", label: "Internet Banking", type: "select", options: ["YES", "NO"], required: true },
];

const tabs = [
  "First Applicant",
  "First Address",
  "Second Applicant",
  "Second Address",
  "Nomination & Services",
  "Preview",
];

const allFields = [
  ...firstPersonalFields,
  ...firstGuardianFields,
  ...firstAddressFields,
  ...secondPersonalFields,
  ...secondGuardianFields,
  ...secondAddressFields,
  ...nominationControlFields,
  ...nomineeFields,
  ...nomineeGuardianFields,
  ...optionalFields,
];

function optionValue(option) {
  return Array.isArray(option) ? option[0] : option;
}

function optionLabel(option) {
  return Array.isArray(option) ? option[1] : option;
}


function createInitialFormData(fields) {
  return fields.reduce((data, field) => {
    data[field.name] = field.type === "select" ? SELECT : "";
    return data;
  }, {});
}

function getAge(dateValue) {
  if (!dateValue) return 0;

  const dob = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return 0;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDifference = today.getMonth() - dob.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < dob.getDate())
  ) {
    age -= 1;
  }

  return age;
}

function formatDate(dateValue) {
  if (!dateValue) return "";

  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;

  return [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    date.getFullYear(),
  ].join("/");
}

function displayOption(field, value) {
  if (!value || value === SELECT) return "-";
  const match = field?.options?.find((option) => optionValue(option) === value);
  return match ? optionLabel(match) : value;
}

function validateField(field, rawValue) {
  const value = String(rawValue ?? "").trim();

  if (field.required) {
    if (field.type === "select" && (!value || value === SELECT)) {
      return `${field.label} is required.`;
    }

    if (!value) {
      return `${field.label} is required.`;
    }
  }

  if (!value || value === SELECT) return "";

  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(value)) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name.endsWith("aadhaarno") && !/^\d{12}$/.test(value)) {
    return "Aadhaar Number must contain exactly 12 digits.";
  }

  if (field.name.endsWith("mobile") && !/^[6-9]\d{9}$/.test(value)) {
    return "Enter a valid 10-digit Indian mobile number.";
  }

  if (field.name.endsWith("pin") && !/^\d{6}$/.test(value)) {
    return "PIN must contain exactly 6 digits.";
  }

  if (field.name.endsWith("pan") && value && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }

  return "";
}

function firstTabWithError(errorNames, tabFieldGroups) {
  return tabFieldGroups.findIndex((fields) =>
    fields.some((field) => errorNames.includes(field.name))
  );
}



const PHP_FORM_ACTION = "/FillMyForm/SBIAOF3/JointForm.php";

export default function Joint() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(() => createInitialFormData(allFields));
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);


  useEffect(() => {
  const controller = new AbortController();

  const loadAutofill = async () => {
    try {
      const response = await fetch(
        `/backend/api/autofill.php?form=${encodeURIComponent("SBIAOF-J3")}`,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to load autofill details.");
      }

      if (!result.found || !result.data) {
        return;
      }

      setFormData((previous) => ({
        ...previous,

        branchname: previous.branchname || result.data.branch || "",
        branchcode: previous.branchcode || result.data.code || "",

        facity: previous.facity || result.data.city || "",
        fadist: previous.fadist || result.data.dist || "",
        fastate: previous.fastate || result.data.state || "",
        fapin: previous.fapin || result.data.pin || "",
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Autofill error:", error);
      }
    }
  };

  loadAutofill();

  return () => {
    controller.abort();
  };
}, []);

  const firstApplicantIsMinor = useMemo(
    () => Boolean(formData.fadob) && getAge(formData.fadob) < 18,
    [formData.fadob],
  );

  const secondApplicantIsMinor = useMemo(
    () => Boolean(formData.sadob) && getAge(formData.sadob) < 18,
    [formData.sadob],
  );

  const hasNomination = formData.isnomination === "YES";

  const nomineeIsMinor = useMemo(
    () => hasNomination && Boolean(formData.ndob) && getAge(formData.ndob) < 18,
    [formData.ndob, hasNomination],
  );

  const tabFieldGroups = useMemo(
    () => [
      firstApplicantIsMinor
        ? [...firstPersonalFields, ...firstGuardianFields]
        : firstPersonalFields,
      firstAddressFields,
      secondApplicantIsMinor
        ? [...secondPersonalFields, ...secondGuardianFields]
        : secondPersonalFields,
      secondAddressFields,
      [
        ...nominationControlFields,
        ...(hasNomination ? nomineeFields : []),
        ...(nomineeIsMinor ? nomineeGuardianFields : []),
        ...optionalFields,
      ],
    ],
    [
      firstApplicantIsMinor,
      secondApplicantIsMinor,
      hasNomination,
      nomineeIsMinor,
    ],
  );

  const visibleFieldsForCurrentTab = tabFieldGroups[currentTab] || [];
  const firstFieldName = visibleFieldsForCurrentTab[0]?.name || "";
  const lastFieldName =
    visibleFieldsForCurrentTab[visibleFieldsForCurrentTab.length - 1]?.name || "";

  useEffect(() => {
    if (!firstFieldName) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(firstFieldName)?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  useEffect(() => {
    return () => {
      if (pdfUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (
      fieldName === lastFieldName &&
      event.key === "Tab" &&
      !event.shiftKey
    ) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const updateField = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = String(value).replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = String(value).replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.name.endsWith("pan")) {
      value = String(value).toUpperCase();
    }

    setFormData((previous) => {
      const next = { ...previous, [field.name]: value };

      if (field.name === "fadob" && getAge(value) >= 18) {
        next.fagname = "";
        next.fagrelation = SELECT;
      }

      if (field.name === "sadob" && getAge(value) >= 18) {
        next.sagname = "";
        next.sagrelation = SELECT;
      }

      if (field.name === "isnomination" && value === "NO") {
        next.nname = "";
        next.nrelation = SELECT;
        next.ndob = "";
        next.ngname = "";
        next.ngage = "";
      }

      if (field.name === "ndob" && getAge(value) >= 18) {
        next.ngname = "";
        next.ngage = "";
      }

      return next;
    });

    setErrors((previous) => ({
      ...previous,
      [field.name]: "",
    }));
  };

  const validateFields = (fields) => {
    const nextErrors = {};

    fields.forEach((field) => {
      const message = validateField(field, formData[field.name]);
      if (message) nextErrors[field.name] = message;
    });

    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return nextErrors;
  };

  const validateCurrentTab = () => {
    return Object.keys(validateFields(visibleFieldsForCurrentTab)).length === 0;
  };

  const copyFirstApplicantAddress = () => {
    
    setFormData((previous) => ({
      ...previous,

      saadd1: previous.faadd1,
      saadd2: previous.faadd2,
      sacity: previous.facity,
      sadist: previous.fadist,
      sastate: previous.fastate,
      sapin: previous.fapin,
    }));


  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!validateCurrentTab()) return;

    if (currentTab === 1) {
      copyFirstApplicantAddress();
    }

    const nextTab = currentTab + 1;

    if (nextTab < tabs.length) {
      setCurrentTab(nextTab);
      setMaxAllowedTab((previous) => Math.max(previous, nextTab));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goPrevious = () => {
    if (currentTab > 0) {
      setCurrentTab((previous) => previous - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getAllRequiredFields = () =>
    tabFieldGroups.slice(0, tabs.length - 1).flat();

  const submitToPhp = async () => {
    setPdfLoading(true);
    setPdfError("");

    try {
      const postData = new FormData();

      Object.entries(formData).forEach(([name, value]) => {
        postData.append(name, value === SELECT ? "" : String(value ?? ""));
      });

      const response = await fetch(PHP_FORM_ACTION, {
        method: "POST",
        body: postData,
        credentials: "include",
        headers: {
          Accept: "application/pdf",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || `PDF generation failed. HTTP status: ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const errorText = await response.text();
        console.error("Invalid PDF response:", errorText);
        throw new Error(errorText || "Server did not return a valid PDF file.");
      }

      const pdfBlob = await response.blob();
      const newPdfUrl = URL.createObjectURL(pdfBlob);

      setPdfUrl((previousUrl) => {
        if (previousUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previousUrl);
        }
        return newPdfUrl;
      });

      setShowPdf(true);
    } catch (error) {
      console.error("PDF preview error:", error);
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  //Open in PDFViewer

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const allErrors = validateFields(getAllRequiredFields());

    if (Object.keys(allErrors).length > 0) {
      const errorTab = firstTabWithError(
        Object.keys(allErrors),
        tabFieldGroups,
      );

      setCurrentTab(errorTab >= 0 ? errorTab : 0);
      return;
    }

    await submitToPhp();
  };

  // // Open in New Tab 

  // const handleSubmit = (event) => {
  //   event.preventDefault();
  //   event.stopPropagation();

  //   const allErrors = validateFields(getAllRequiredFields());

  //   if (Object.keys(allErrors).length > 0) {
  //     const errorTab = firstTabWithError(
  //       Object.keys(allErrors),
  //       tabFieldGroups,
  //     );

  //     setCurrentTab(errorTab >= 0 ? errorTab : 0);
  //     return;
  //   }

  //   setPdfError("");

  //   try {
  //     const form = document.createElement("form");

  //     form.method = "POST";
  //     form.action = PHP_FORM_ACTION;
  //     form.target = "_blank";
  //     form.style.display = "none";

  //     Object.entries(formData).forEach(([name, value]) => {
  //       const input = document.createElement("input");

  //       input.type = "hidden";
  //       input.name = name;
  //       input.value = value === SELECT ? "" : String(value ?? "");

  //       form.appendChild(input);
  //     });

  //     document.body.appendChild(form);
  //     form.submit();

  //     setTimeout(() => {
  //       if (document.body.contains(form)) {
  //         document.body.removeChild(form);
  //       }
  //     }, 100);
  //   } catch (error) {
  //     console.error("Print error:", error);
  //     setPdfError(error.message || "Unable to open PDF.");
  //   }
  // };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <Select
            value={formData[field.name] || SELECT}
            onValueChange={(value) => updateField(field, value)}
          >
            <SelectTrigger
              id={field.name}
              className={error ? "border-red-500" : ""}
              onKeyDown={(event) =>
                handleLastFieldKeyDown(event, field.name)
              }
            >
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={SELECT}>SELECT</SelectItem>
              {field.options.map((option) => (
                <SelectItem
                  key={optionValue(option)}
                  value={optionValue(option)}
                >
                  {optionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name] || ""}
            placeholder={field.placeholder || field.label}
            maxLength={field.maxLength}
            onChange={(event) => updateField(field, event.target.value)}
            onKeyDown={(event) =>
              handleLastFieldKeyDown(event, field.name)
            }
            className={error ? "border-red-500" : ""}
            autoComplete="on"
          />
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  const firstApplicantRows = buildApplicantRows(
    "fa",
    formData,
    firstPersonalFields,
    firstGuardianFields,
    firstAddressFields,
    firstApplicantIsMinor,
  );

  const secondApplicantRows = buildApplicantRows(
    "sa",
    formData,
    secondPersonalFields,
    secondGuardianFields,
    secondAddressFields,
    secondApplicantIsMinor,
  );

  const nominationRows = [
    ["Is Nomination?", formData.isnomination],
    ...(hasNomination
      ? [
          ["Nominee Name", formData.nname],
          ["Relation", formData.nrelation],
          ["Date of Birth", formatDate(formData.ndob)],
          ["Guardian's Name", nomineeIsMinor ? formData.ngname : "-"],
          ["Guardian's Age", nomineeIsMinor ? formData.ngage : "-"],
        ]
      : []),
    ["Mode of Operation", displayOption(optionalFields[0], formData.mod)],
    ["ATM", formData.atm],
    ["Cheque Book", formData.cbook],
    ["Internet Banking", formData.ibanking],
  ];

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Joint Account Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 SBI Joint Account Opening Form
            </CardTitle>
            <p className="text-muted-foreground">
              Enter both applicants&apos; details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={currentTab === index ? "default" : "outline"}
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 && (
          <ApplicantSection
            title="👤 First Applicant Personal Details"
            personalFields={firstPersonalFields}
            guardianFields={firstGuardianFields}
            isMinor={firstApplicantIsMinor}
            renderField={renderField}
          />
        )}

        {currentTab === 1 && (
          <FieldsCard
            title="🏠 First Applicant Address Details"
            fields={firstAddressFields}
            renderField={renderField}
          />
        )}

        {currentTab === 2 && (
          <ApplicantSection
            title="👤 Second Applicant Personal Details"
            personalFields={secondPersonalFields}
            guardianFields={secondGuardianFields}
            isMinor={secondApplicantIsMinor}
            renderField={renderField}
          />
        )}

        {currentTab === 3 && (
          <FieldsCard
            title="🏠 Second Applicant Address Details"
            fields={secondAddressFields}
            renderField={renderField}
          />
        )}

        {currentTab === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>👥 Nomination and Optional Services</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <FieldsGrid
                fields={nominationControlFields}
                renderField={renderField}
              />

              {hasNomination && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Nomination Details</h3>
                  <FieldsGrid fields={nomineeFields} renderField={renderField} />

                  {nomineeIsMinor && (
                    <>
                      <h3 className="text-lg font-semibold">
                        Nominee Guardian Details
                      </h3>
                      <FieldsGrid
                        fields={nomineeGuardianFields}
                        renderField={renderField}
                      />
                    </>
                  )}
                </div>
              )}

              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Optional Services</h3>
                <FieldsGrid fields={optionalFields} renderField={renderField} />
              </div>
            </CardContent>
          </Card>
        )}

        {currentTab === 5 && (
          <Card>
            <CardHeader>
              <CardTitle>✅ Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <PreviewSection
                title="First Applicant Details"
                rows={firstApplicantRows}
              />
              <PreviewSection
                title="Second Applicant Details"
                rows={secondApplicantRows}
              />
              <PreviewSection
                title="Nomination and Services"
                rows={nominationRows}
              />
            </CardContent>
          </Card>
        )}

        {pdfLoading && (
          <div className="mt-6 text-center">
            Generating PDF, please wait...
          </div>
        )}

        {pdfError && (
          <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">
            {pdfError}
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0 || pdfLoading}
          >
            ← Previous
          </Button>

          {currentTab === tabs.length - 1 ? (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading ? "Generating PDF..." : "Submit"}
            </Button>
          ) : (
            <Button
              ref={nextButtonRef}
              type="button"
              onClick={goNext}
              disabled={pdfLoading}
            >
              {currentTab === tabs.length - 2 ? "Preview" : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldsGrid({ fields, renderField }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {fields.map(renderField)}
    </div>
  );
}

function FieldsCard({ title, fields, renderField }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldsGrid fields={fields} renderField={renderField} />
      </CardContent>
    </Card>
  );
}

function ApplicantSection({
  title,
  personalFields,
  guardianFields,
  isMinor,
  renderField,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        <FieldsGrid fields={personalFields} renderField={renderField} />

        {isMinor && (
          <div>
            <h3 className="mb-4 text-lg font-semibold">Guardian Details</h3>
            <FieldsGrid fields={guardianFields} renderField={renderField} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PreviewSection({ title, rows }) {
  return (
    <section className="rounded-2xl border p-6">
      <h3 className="mb-6 text-center text-xl font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value && value !== SELECT ? value : "-"}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function buildApplicantRows(
  prefix,
  formData,
  personalFields,
  guardianFields,
  addressFields,
  isMinor,
) {
  const fieldMap = Object.fromEntries(
    [...personalFields, ...guardianFields, ...addressFields].map((field) => [
      field.name,
      field,
    ]),
  );

  return [
    ["CIF Number", formData[`${prefix}cifno`]],
    ["Account Number", formData[`${prefix}acno`]],
    ["Applicant Name", formData[`${prefix}name`]],
    ["Date of Birth", formatDate(formData[`${prefix}dob`])],
    ["Gender", formData[`${prefix}gender`]],
    ["Marital Status", formData[`${prefix}mstatus`]],
    ["Aadhaar Number", formData[`${prefix}aadhaarno`]],
    ["PAN Number", formData[`${prefix}pan`]],
    ["Father's Name", formData[`${prefix}fname`]],
    ["Guardian's Name", isMinor ? formData[`${prefix}gname`] : "-"],
    [
      "Guardian's Relation",
      isMinor ? formData[`${prefix}grelation`] : "-",
    ],
    ["Address Line 1", formData[`${prefix}add1`]],
    ["Address Line 2", formData[`${prefix}add2`]],
    ["City", formData[`${prefix}city`]],
    ["District", formData[`${prefix}dist`]],
    ["State", formData[`${prefix}state`]],
    ["PIN", formData[`${prefix}pin`]],
    ["Birth Place", formData[`${prefix}bplace`]],
    ["Mobile", formData[`${prefix}mobile`]],
    ["Annual Income", formData[`${prefix}income`]],
    ["Religion", formData[`${prefix}religion`]],
    ["Category", formData[`${prefix}category`]],
    [
      "Qualification",
      displayOption(
        fieldMap[`${prefix}qualification`],
        formData[`${prefix}qualification`],
      ),
    ],
    [
      "Occupation",
      displayOption(
        fieldMap[`${prefix}occupation`],
        formData[`${prefix}occupation`],
      ),
    ],
  ];
}
